import React, { PropsWithChildren, useState } from 'react';

import { Alert, Box, Divider, Stack, Typography } from '@mui/material';

import { diffDays, formatDateOrdinalWithClock, getTimeDifference } from '~/services/TimeService';

import { AuditLog as IAuditLog } from '~/types/responses';

import { TraverseObject } from '../DebugModal/utils';

type Props = PropsWithChildren<{
  auditLog: IAuditLog;
  showFull?: boolean;
}>;

const ActionMapLabels: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
  Read: 'success',
  Create: 'success',
  Update: 'info',
  Delete: 'error',
};

export function AuditLog({ auditLog, children }: Props) {
  const [showFull, setShowFull] = useState(false);

  const { actor, resource, model, meta, payload, request_id } = auditLog;
  const actorEmail = actor.email ?? actor.id ?? 'Unknown';
  const hasModel = !!model;
  const modelId = payload?.params?.modelId || payload?.params?.id || payload?.params?.model_id || model?.id;

  return (
    <>
      <Divider orientation="horizontal" />
      <Stack spacing={1} paddingLeft="1em">
        <Stack
          direction="row"
          spacing={2}
          justifyContent={showFull && 'space-between'}
          alignItems="center"
          onClick={() => setShowFull(!showFull)}
          sx={{
            cursor: 'pointer',
          }}
          title={showFull ? 'Hide Details' : 'Show Details'}
        >
          <Typography>
            {diffDays(new Date(), new Date(auditLog.timestamp)) > 1
              ? formatDateOrdinalWithClock(auditLog.timestamp)
              : getTimeDifference(new Date(auditLog.timestamp), new Date(), true, true)}
          </Typography>
          <Typography variant="caption">
            <Alert
              icon={false}
              variant="outlined"
              severity={ActionMapLabels[auditLog.action] ?? 'info'}
              sx={{
                padding: '0.5em',
              }}
            >
              {auditLog.action}
            </Alert>
          </Typography>
          <Typography>{actorEmail}</Typography>
          {!showFull && hasModel && (
            <Typography sx={{ border: '1px solid', borderColor: 'divider', padding: '0.5rem 1rem' }}>Model</Typography>
          )}
          {!showFull && (
            <Typography fontSize="0.8rem" color="text.secondary" sx={{ justifySelf: 'flex-end' }}>
              {resource}
            </Typography>
          )}
          {showFull && modelId && <Typography>ModelId: {String(modelId)}</Typography>}
          {showFull && <Typography>Request: {request_id}</Typography>}
        </Stack>
        {showFull && (
          <Stack>
            <Typography variant="h6">Properties</Typography>
            {auditLog.originator && <TraverseObject obj={{ originator: auditLog.originator }} />}
            <TraverseObject obj={{ service: auditLog.service }} />
            <TraverseObject obj={{ resource: auditLog.resource }} />
            <TraverseObject obj={{ action: auditLog.action }} />
            <TraverseObject obj={{ severity: auditLog.severity }} />
            {auditLog.request_id && <TraverseObject obj={{ request_id: auditLog.request_id }} />}
            {auditLog.le_request_id && <TraverseObject obj={{ le_request_id: auditLog.le_request_id }} />}
            {auditLog.trace_id && <TraverseObject obj={{ trace_id: auditLog.trace_id }} />}
            <TraverseObject
              obj={{ actor: `${auditLog.actor.email ?? 'Unknown'} (${auditLog.actor.id ?? 'Unknown'})` }}
            />
            {auditLog.actor.acted_as && (
              <TraverseObject
                obj={{
                  acted_as: `${auditLog.actor.acted_as.email ?? 'Unknown'} (${
                    auditLog.actor.acted_as.id ?? 'Unknown'
                  })`,
                }}
              />
            )}
            {meta && (
              <>
                <Typography variant="h6">Meta</Typography>
                <TraverseObject obj={meta} />
              </>
            )}
            {payload && (
              <>
                <Typography variant="h6">Payload</Typography>
                <TraverseObject obj={payload} />
              </>
            )}
            {hasModel && (
              <>
                <Typography variant="h6">Model Changes</Typography>
                <TraverseObject obj={model} />
              </>
            )}
            <Box paddingLeft="2em">{children}</Box>
          </Stack>
        )}
      </Stack>
    </>
  );
}
